import { useEffect, useState, useRef } from 'react';

import { Evaluation } from '@/models/evaluations';
import Worker from '@/utils/pdf/pdf.worker?worker';

export const useRenderPDF = (pages: Evaluation.EvaluationPDFResponse[]) => {
  const [url, setUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const prevPagesLengthRef = useRef<number>(0);
  const workerRef = useRef<Worker | null>(null);
  const objectUrlRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!pages || pages.length === 0) {
      setUrl('');
      setError(undefined);
      prevPagesLengthRef.current = 0;
      return;
    }

    if (pages.length === prevPagesLengthRef.current && url) {
      return;
    }

    prevPagesLengthRef.current = pages.length;

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = undefined;
    }

    if (workerRef.current) {
      workerRef.current.terminate();
    }

    setLoading(true);
    setError(undefined);

    const worker = new Worker();
    workerRef.current = worker;

    worker.postMessage(pages);

    worker.onmessage = (event) => {
      const { success, blob, error: workerError } = event.data;

      if (!success) {
        const errorMessage =
          workerError?.toString() ||
          'Unknown error occurred while rendering PDF';
        setError(errorMessage);
        setUrl('');
        setLoading(false);
        return;
      }

      if (!blob) {
        setError('No blob returned from worker');
        setUrl('');
        setLoading(false);
        return;
      }

      try {
        objectUrlRef.current = URL.createObjectURL(blob);
        setUrl(objectUrlRef.current);
        setLoading(false);
      } catch (err) {
        setError(`Failed to create object URL: ${err}`);
        setLoading(false);
      }
    };

    worker.onerror = (event) => {
      setError(`Worker error: ${event.message}`);
      setLoading(false);
    };
  }, [pages.length, url]);

  useEffect(
    () => () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    },
    []
  );

  return { url, loading, error };
};
